import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, Baby, Users, Award, CheckCircle, Play, 
  ChevronRight, Globe, Building2, BookOpen, Star, MapPin,
  Phone, Mail, Send, MessageCircle, Target, TrendingUp, Headphones, UserCheck, Youtube,
  Calendar, ArrowRight, Eye, Facebook
} from 'lucide-react';
import { cmsAPI, settingsAPI, contactAPI, tarifsAPI, analyticsAPI, blogAPI } from '../../lib/api';
import TestimonialsCarousel from '../ui/TestimonialsCarousel';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import Layout from '../layout/Layout';
import YouTubeLazy from '../ui/YouTubeLazy';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/9i39bysf_475444528_1384443796015613_5677894820733752221_n%20%281%29.jpg';

// Logo pour hero et footer (fond sombre)
const LOGO_DARK_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/o5ssfxmc_english%20hero.png';

// Photo de la plaque du centre
const PLAQUE_IMAGE = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/b94fn2ln_98162526_758830517987738_1887147396513660928_n.jpg';

// Images du centre - Photos fournies
const SCHOOL_IMAGES = {
  classroom: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/sir01hxa_2021-09-05.webp',
  studentsWithCertificates: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/6p8f6v3h_2022-09-25.webp',
  storefront: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/ongrsvb1_2022-10-25.webp',
  graduation: 'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/8l5pl8n1_2025-06-18.webp',
};

// Image héro du site original
const HERO_IMAGE_URL = SCHOOL_IMAGES.storefront;

// Vidéos YouTube de présentation
const YOUTUBE_VIDEOS = [
  {
    id: 1,
    title: "Présentation du centre",
    description: "Découvrez The English Speaking Center d'Istres, votre partenaire pour l'apprentissage de l'anglais",
    embedUrl: "https://www.youtube.com/embed/vz3NH7uA4Pc",
    youtubeId: "vz3NH7uA4Pc"
  },
  {
    id: 2, 
    title: "Qu'est-ce que Qualiopi ?",
    description: "Comprendre la certification Qualiopi et son importance pour votre formation CPF",
    embedUrl: "https://www.youtube.com/embed/JO19qCv9NAo",
    youtubeId: "JO19qCv9NAo"
  }
];

// Video ID pour le hero background
const HERO_VIDEO_ID = "vz3NH7uA4Pc";

// Carousel slides pour les différents publics
const CAROUSEL_SLIDES = [
  {
    id: 1,
    image: SCHOOL_IMAGES.graduation,
    title: "Enfants & Adolescents",
    subtitle: "Cambridge Young Learners",
    description: "Starters, Movers, Flyers - Un apprentissage ludique dès 6 ans",
    badge: "Dès 6 ans",
    link: "/programmes/enfants"
  },
  {
    id: 2,
    image: SCHOOL_IMAGES.studentsWithCertificates,
    title: "Adultes",
    subtitle: "Formations certifiantes",
    description: "Key, Preliminary, First, Advanced - Tous niveaux CECRL",
    badge: "CPF Éligible",
    link: "/programmes/adultes"
  },
  {
    id: 3,
    image: SCHOOL_IMAGES.classroom,
    title: "Entreprises",
    subtitle: "Formations sur-mesure",
    description: "Montez en compétences avec votre CPF ou le plan de formation",
    badge: "Pro",
    link: "/contact"
  },
  {
    id: 4,
    image: SCHOOL_IMAGES.storefront,
    title: "Notre Centre",
    subtitle: "À Istres depuis 2015",
    description: "Centre de préparation Cambridge agréé et certifié Qualiopi",
    badge: "Certifié",
    link: "/#centre"
  }
];

const HomePageContent = () => {
  const [sections, setSections] = useState([]);
  const [settings, setSettings] = useState({});
  const [contactForm, setContactForm] = useState({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    loadFeaturedArticles();
    // Track visit
    analyticsAPI.trackVisit().catch(() => {});
    // Auto-advance carousel
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % CAROUSEL_SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const [sectionsRes, settingsRes] = await Promise.all([
        cmsAPI.getSections('home'),
        settingsAPI.get()
      ]);
      setSections(sectionsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadFeaturedArticles = async () => {
    try {
      const res = await blogAPI.getFeatured(3);
      setFeaturedArticles(res.data);
    } catch (error) {
      console.error('Error loading featured articles:', error);
    }
  };

  const getSection = (key) => sections.find(s => s.section_key === key) || {};

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(contactForm);
      toast({ title: 'Message envoyé', description: 'Nous vous répondrons dans les plus brefs délais.' });
      setContactForm({ nom: '', prenom: '', email: '', telephone: '', message: '' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Une erreur est survenue.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const heroSection = getSection('hero');
  const certifications = getSection('certifications');
  const presentation = getSection('presentation');
  const pointsForts = getSection('points_forts');
  const cpfSection = getSection('cpf');
  const qualiopiSection = getSection('qualiopi');
  const cambridgeSection = getSection('cambridge');
  const statsSection = getSection('stats');
  const contactSection = getSection('contact');

  const iconMap = {
    GraduationCap: GraduationCap,
    Baby: Baby,
    Users: Users,
  };

  // Points clés de la méthode Cambridge
  const cambridgeKeyPoints = [
    { title: "Communication verbale", icon: MessageCircle, description: "Développez vos compétences orales en anglais" },
    { title: "Exercices personnalisés", icon: Target, description: "Programme adapté à votre niveau et objectifs" },
    { title: "Apprentissage mixte", icon: BookOpen, description: "Combinaison de théorie et pratique" },
    { title: "Suivi de l'évolution", icon: TrendingUp, description: "Évaluation continue de vos progrès" },
    { title: "Accompagnement aux objectifs", icon: UserCheck, description: "Support personnalisé vers la réussite" },
  ];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section relative flex items-center justify-center min-h-[70vh] md:min-h-screen" data-testid="hero-section">
        {/* Background Video with 30% visibility */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#285BBB]/70 via-[#1a3a7a]/65 to-[#2C3C65]/70 z-10" />
          <div className="absolute inset-0 w-full h-full">
            <iframe
              src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playlist=${HERO_VIDEO_ID}&playsinline=1`}
              className="absolute w-[400%] h-[400%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:w-[200%] md:h-[200%]"
              style={{ 
                pointerEvents: 'none',
              }}
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="Background video"
            />
          </div>
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <motion.img
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            src={LOGO_DARK_URL}
            alt="The English Speaking Center"
            className="h-28 md:h-36 w-auto mx-auto mb-8"
          />
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {heroSection.title || "L'anglais est un problème pour vous ?"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto"
          >
            {heroSection.subtitle || "Prenez de l'avance avec The English Speaking Center !"}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/inscription"
              className="px-8 py-4 bg-[#ff3a2d] text-white font-bold rounded-lg hover:bg-[#e6352a] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              data-testid="hero-inscription-btn"
            >
              Je m'inscris
            </Link>
            <a
              href="#centre"
              className="px-8 py-4 bg-white/10 backdrop-blur text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30"
              data-testid="hero-discover-btn"
            >
              En savoir plus
            </a>
          </motion.div>
        </div>
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Certifications Band */}
      <section className="py-8 bg-white" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {(certifications.items || []).map((cert, idx) => (
              <motion.img
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                viewport={{ once: true }}
                src={cert.image}
                alt={cert.name}
                className="h-16 md:h-20 w-auto object-contain"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Modern Carousel - Nos Publics */}
      <section className="py-16 bg-white" data-testid="carousel-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Pour tous les publics</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C3C65] mt-3">
              Des formations adaptées à chacun
            </h2>
          </div>
          
          {/* Carousel Container - with padding for external arrows */}
          <div className="relative px-0 md:px-8 lg:px-10">
            {/* Main Carousel */}
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <div 
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {CAROUSEL_SLIDES.map((slide) => (
                  <div key={slide.id} className="w-full flex-shrink-0 relative">
                    <div className="aspect-[16/7] md:aspect-[21/9] relative">
                      <img 
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
                      
                      {/* Content */}
                      <div className="absolute inset-0 flex items-center">
                        <div className="px-8 md:px-16 max-w-2xl">
                          <span className="inline-block px-4 py-1 bg-[#ff3a2d] text-white text-sm font-semibold rounded-full mb-4">
                            {slide.badge}
                          </span>
                          <h3 className="text-3xl md:text-5xl font-bold text-white mb-2">
                            {slide.title}
                          </h3>
                          <p className="text-xl md:text-2xl text-blue-200 font-medium mb-4">
                            {slide.subtitle}
                          </p>
                          <p className="text-white/80 text-base md:text-lg mb-6 max-w-lg">
                            {slide.description}
                          </p>
                          <Link 
                            to={slide.link}
                            className="inline-flex items-center px-6 py-3 bg-white text-[#2C3C65] font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            En savoir plus
                            <ChevronRight className="ml-2 h-5 w-5" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows - Positioned outside on desktop */}
            <button 
              onClick={() => setCurrentSlide((prev) => (prev - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length)}
              className="hidden md:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 bg-white hover:bg-gray-100 rounded-full shadow-lg items-center justify-center transition-all hover:scale-110 z-10 border border-gray-200"
              aria-label="Précédent"
            >
              <ChevronRight className="h-5 w-5 lg:h-6 lg:w-6 text-[#2C3C65] rotate-180" />
            </button>
            <button 
              onClick={() => setCurrentSlide((prev) => (prev + 1) % CAROUSEL_SLIDES.length)}
              className="hidden md:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 lg:w-12 lg:h-12 bg-white hover:bg-gray-100 rounded-full shadow-lg items-center justify-center transition-all hover:scale-110 z-10 border border-gray-200"
              aria-label="Suivant"
            >
              <ChevronRight className="h-5 w-5 lg:h-6 lg:w-6 text-[#2C3C65]" />
            </button>
            
            {/* Mobile Navigation - Arrows below content */}
            <div className="flex md:hidden justify-center items-center gap-6 mt-4">
              <button 
                onClick={() => setCurrentSlide((prev) => (prev - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length)}
                className="w-10 h-10 bg-[#285BBB] hover:bg-[#1e4a9a] rounded-full shadow-lg flex items-center justify-center transition-all"
                aria-label="Précédent"
              >
                <ChevronRight className="h-5 w-5 text-white rotate-180" />
              </button>
              
              {/* Mobile Dots */}
              <div className="flex gap-2">
                {CAROUSEL_SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      currentSlide === idx 
                        ? 'w-8 bg-[#285BBB]' 
                        : 'w-2.5 bg-gray-300'
                    }`}
                    aria-label={`Slide ${idx + 1}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={() => setCurrentSlide((prev) => (prev + 1) % CAROUSEL_SLIDES.length)}
                className="w-10 h-10 bg-[#285BBB] hover:bg-[#1e4a9a] rounded-full shadow-lg flex items-center justify-center transition-all"
                aria-label="Suivant"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </div>
            
            {/* Desktop Dots Indicator */}
            <div className="hidden md:flex justify-center gap-3 mt-6">
              {CAROUSEL_SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    currentSlide === idx 
                      ? 'w-10 bg-[#285BBB]' 
                      : 'w-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Aller au slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section id="centre" className="py-20 bg-white" data-testid="presentation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-[#285BBB] font-semibold uppercase tracking-wider">The English Speaking Center</span>
              <h2 className="text-4xl font-bold text-[#2C3C65] mt-3 mb-6">
                {presentation.title || "Le centre de formation"}
              </h2>
              <p className="text-lg text-[#6777A1] leading-relaxed mb-8">
                {presentation.content || "The English Speaking Center d'Istres vous accueille pour rejoindre nos cours d'anglais. Notre centre s'engage à vous offrir une expérience d'apprentissage de l'anglais excellente et différente avec des résultats garantis."}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/demande-info"
                  className="inline-flex items-center px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#2C3C65] transition-colors"
                >
                  Demande d'informations
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 border-2 border-[#285BBB] text-[#285BBB] font-semibold rounded-lg hover:bg-[#285BBB] hover:text-white transition-colors"
                >
                  Nous contacter
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={SCHOOL_IMAGES.classroom}
                    alt="Salle de classe The English Speaking Center"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={PLAQUE_IMAGE}
                    alt="Plaque The English Speaking Center - 9 Allée Jean Jaurès, 13800 Istres"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#ff3a2d]/20 rounded-full blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#285BBB]/20 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="py-20 bg-[#2C3C65]" data-testid="videos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-white text-sm font-medium mb-4">
                <Youtube className="h-4 w-4 mr-2 text-red-500" />
                Découvrez-nous en vidéo
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                The English Speaking Center en images
              </h2>
            </motion.div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {YOUTUBE_VIDEOS.map((video, idx) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-xl"
              >
                <div className="aspect-video">
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#2C3C65] mb-2">{video.title}</h3>
                  <p className="text-[#6777A1]">{video.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Points Forts Section */}
      <section className="py-20 bg-[#F5F7FF]" data-testid="points-forts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#285BBB] font-semibold uppercase tracking-wider">En quelques mots</span>
            <h2 className="text-4xl font-bold text-[#2C3C65] mt-3">
              {pointsForts.title || "Nos points forts"}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(pointsForts.items || [
              { title: "Enseignement adapté à tous niveaux", description: "Quels que soient votre âge et votre expérience.", icon: "GraduationCap" },
              { title: "Apprentissage dès le jeune âge", description: "Des cours spécialement conçus pour les enfants.", icon: "Baby" },
              { title: "Apprentissage interactif", description: "Méthodes innovantes et engageantes.", icon: "Users" },
            ]).map((item, idx) => {
              const IconComponent = iconMap[item.icon] || GraduationCap;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.15 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow card-hover"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2C3C65] mb-3">{item.title}</h3>
                  <p className="text-[#6777A1] leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CPF Section */}
      <section className="py-20 bg-white" data-testid="cpf-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] p-10 rounded-2xl text-white">
                <Building2 className="h-12 w-12 mb-6" />
                <h3 className="text-2xl font-bold mb-4">Financement CPF</h3>
                <p className="text-blue-100 mb-6">
                  Utilisez votre Compte Personnel de Formation pour financer votre apprentissage de l'anglais.
                </p>
                <ul className="space-y-3">
                  {['Formations éligibles CPF', 'Accompagnement personnalisé', 'Certification reconnue'].map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-3 text-green-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://www.moncompteformation.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-8 px-6 py-3 bg-white text-[#285BBB] font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Accéder à Mon Compte Formation
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Financement CPF</span>
              <h2 className="text-4xl font-bold text-[#2C3C65] mt-3 mb-6">
                {cpfSection.title || "CPF Entreprise"}
              </h2>
              <p className="text-lg text-[#6777A1] leading-relaxed mb-6">
                {cpfSection.content || "Toutes nos formations d'anglais sont éligibles au CPF et adaptées à tous les profils. Nous vous accompagnons dans vos démarches."}
              </p>
              <div className="bg-[#F5F7FF] p-6 rounded-xl">
                <h4 className="font-semibold text-[#2C3C65] mb-3">Vous êtes une entreprise ?</h4>
                <p className="text-[#6777A1] text-sm">
                  Nous vous aidons à constituer votre demande de CPF anglais jusqu'à l'accord définitif.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Qualiopi Section */}
      <section className="py-20 bg-[#F5F7FF]" data-testid="qualiopi-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Garant de qualité</span>
              <h2 className="text-4xl font-bold text-[#2C3C65] mt-3 mb-6">
                {qualiopiSection.title || "Centre certifié Qualiopi"}
              </h2>
              <p className="text-lg text-[#6777A1] leading-relaxed mb-8">
                {qualiopiSection.content || "Qualiopi garantit la qualité des formations CPF sur différents critères. Cette certification a été créée par le ministère du Travail pour harmoniser la qualité de l'offre de formation professionnelle."}
              </p>
              <div className="flex items-center space-x-6">
                <img
                  src="/qualiopi-new.png"
                  alt="Qualiopi"
                  className="h-24 w-auto"
                />
                <div>
                  <p className="font-semibold text-[#2C3C65]">Certification Qualiopi</p>
                  <p className="text-sm text-[#6777A1]">Actions de formation</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { title: 'Information', desc: 'Informations claires et accessibles' },
                { title: 'Prestations', desc: 'Formations adaptées aux besoins' },
                { title: 'Formateurs', desc: 'Intervenants qualifiés' },
                { title: 'Évaluation', desc: 'Suivi et amélioration continue' },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl shadow-md"
                >
                  <Award className="h-8 w-8 text-[#285BBB] mb-3" />
                  <h4 className="font-semibold text-[#2C3C65]">{item.title}</h4>
                  <p className="text-sm text-[#6777A1] mt-1">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cambridge Section */}
      <section className="py-20 bg-white" data-testid="cambridge-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Préparation aux examens Cambridge</span>
            <h2 className="text-4xl font-bold text-[#2C3C65] mt-3">
              {cambridgeSection.title || "Certification Cambridge"}
            </h2>
            <p className="text-lg text-[#6777A1] mt-4 max-w-3xl mx-auto">
              {cambridgeSection.content || "Préparer une certification Cambridge English aide les apprenants à développer les compétences dont ils ont besoin."}
            </p>
          </div>
          
          {/* Logo Cambridge Assessment */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <img 
                src="/cambridge-assessment.png" 
                alt="Cambridge Assessment" 
                className="h-16 w-auto mx-auto"
              />
            </div>
          </div>
          
          {/* Diagramme CEFR */}
          <div className="mb-12">
            <img 
              src="/cambridge-cefr.jpg" 
              alt="Cambridge English Qualifications - CEFR Levels" 
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
            />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {(cambridgeSection.items || [
              { title: 'Certifications reconnues internationalement', description: 'Reconnues par des milliers d\'universités et d\'employeurs.' },
              { title: 'Accent sur la communication', description: 'Exercices basés sur des situations réelles.' },
              { title: 'Standards internationaux', description: 'Alignées avec le CECRL.' },
            ]).map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-[#F5F7FF] rounded-2xl"
              >
                <div className="w-16 h-16 bg-[#285BBB] rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#2C3C65] mb-3">{item.title}</h3>
                <p className="text-[#6777A1]">{item.description}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/cambridge"
              className="inline-flex items-center px-8 py-4 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#2C3C65] transition-colors"
            >
              Découvrir nos préparations
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Témoignages & Avis Section */}
      <section className="py-20 bg-[#F5F7FF]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Ce que disent nos élèves</span>
            <h2 className="text-4xl font-bold text-[#2C3C65] mt-3">Témoignages & Avis</h2>
            <p className="text-lg text-[#6777A1] mt-4 max-w-3xl mx-auto">
              Découvrez les témoignages de nos élèves et leurs parcours de réussite
            </p>
          </div>
          
          {/* Carrousel Avis Google */}
          <div className="mb-16">
            <div className="flex justify-center items-center gap-4 mb-8">
              <div className="bg-white px-6 py-4 rounded-2xl shadow-lg flex items-center gap-4">
                <img 
                  src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png"
                  alt="Google"
                  className="h-10 w-10"
                />
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    Note 5.0 sur Google
                    {(settings.testimonials || []).length > 0 && ` (${(settings.testimonials || []).length} avis affichés)`}
                  </p>
                </div>
              </div>
            </div>

            {(settings.testimonials || []).length === 0 ? (
              <div className="text-center text-[#6777A1] py-8" data-testid="no-testimonials">
                <p className="mb-4">Les avis seront ajoutés prochainement. En attendant&nbsp;:</p>
                <a 
                  href={settings.google_reviews_url || "https://www.google.com/search?q=the+english+speaking+center+istres+avis"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#285BBB] text-white px-6 py-3 rounded-xl hover:bg-[#1e4a9a] transition-colors font-medium"
                >
                  <Star className="h-5 w-5" />
                  Voir nos avis Google
                </a>
              </div>
            ) : (
              <TestimonialsCarousel testimonials={settings.testimonials || []} />
            )}
            
            <div className="text-center mt-8">
              <a 
                href={settings.google_reviews_url || "https://share.google/EoZfmBd0nBOUZHT8w"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#285BBB] text-white px-6 py-3 rounded-xl hover:bg-[#1e4a9a] transition-colors font-medium"
              >
                <Star className="h-5 w-5" />
                Voir tous les avis Google
              </a>
            </div>
          </div>

          {/* Vidéos témoignages Jill & Joey */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { id: 'qknCL8qUrc8', testid: 'video-jill' },
                { id: '8s2pAQwPdpo', testid: 'video-joey' },
              ].map((v) => (
                <YouTubeLazy key={v.id} videoId={v.id} testid={v.testid} />
              ))}
            </div>
          </div>

          {/* Photos de remise de diplômes */}
          <div>
            <h3 className="text-2xl font-bold text-[#2C3C65] mb-8 text-center">
              Remise des diplômes Cambridge
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/8l5pl8n1_2025-06-18.webp',
                'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/ongrsvb1_2022-10-25.webp',
                'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/6p8f6v3h_2022-09-25.webp',
                'https://customer-assets.emergentagent.com/job_7ad81f0f-836b-4ad1-b763-41e7e6b8cfe2/artifacts/sir01hxa_2021-09-05.webp',
              ].map((img, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="aspect-square rounded-xl overflow-hidden shadow-lg"
                >
                  <img 
                    src={img}
                    alt={`Remise de diplômes Cambridge ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a 
                href="https://www.facebook.com/TheEnglishSpeakingCenter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1877F2] text-white rounded-xl hover:bg-[#166fe5] transition-colors font-medium"
              >
                <Facebook className="h-5 w-5" />
                Voir plus sur Facebook
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#285BBB] to-[#2C3C65]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white">
              L'anglais dans le monde
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(statsSection.items || [
              { number: '2B+', label: 'De personnes parlent anglais', description: 'L\'importance de l\'anglais réside dans sa diffusion mondiale.' },
              { number: '67+', label: 'Nations avec l\'anglais officiel', description: 'Plus de 67 nations ont adopté l\'anglais comme première langue officielle.' },
              { number: '27+', label: 'Pays avec l\'anglais en 2ème langue', description: 'L\'anglais est la seconde langue officielle dans plus de 27 pays.' },
            ]).map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl text-center text-white border border-white/20"
              >
                <Globe className="h-10 w-10 mx-auto mb-4 text-blue-200" />
                <div className="text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg font-semibold mb-3">{stat.label}</div>
                <p className="text-blue-200 text-sm">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nos Réussites - Blog Section */}
      <section className="py-20 bg-[#F5F7FF]" data-testid="blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Témoignages & Actualités</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2C3C65] mt-3 mb-4">
                Nos réussites
              </h2>
              <p className="text-[#6777A1] max-w-2xl mx-auto">
                Découvrez les parcours de nos élèves et suivez l'actualité du centre
              </p>
            </motion.div>
          </div>

          {featuredArticles.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all group"
                >
                  <Link to={`/blog/${article.slug}`}>
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={article.image_url || SCHOOL_IMAGES.studentsWithCertificates} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {article.category_name && (
                        <span className="absolute top-3 left-3 px-3 py-1 bg-[#285BBB] text-white text-xs font-semibold rounded-full">
                          {article.category_name}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-[#2C3C65] mb-2 group-hover:text-[#285BBB] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-[#6777A1] text-sm mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>

                      {/* Read more */}
                      <div className="flex items-center text-[#285BBB] text-sm font-semibold group-hover:gap-2 transition-all">
                        <span>Lire la suite</span>
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            /* État vide - affiche les photos de l'école en attendant des articles */
            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="md:col-span-2 relative group overflow-hidden rounded-2xl shadow-xl"
              >
                <img 
                  src={SCHOOL_IMAGES.graduation}
                  alt="Élèves avec leurs diplômes Cambridge"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">🎓 Remise des diplômes 2025</h3>
                  <p className="text-white/80">Nos élèves célèbrent leurs certifications Cambridge</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-2xl shadow-xl"
              >
                <img 
                  src={SCHOOL_IMAGES.studentsWithCertificates}
                  alt="Groupe d'élèves devant le centre"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-lg font-bold mb-1">🏆 Session Cambridge 2022</h3>
                  <p className="text-white/80 text-sm">Examens réussis !</p>
                </div>
              </motion.div>
            </div>
          )}

          {/* CTA vers le blog */}
          <div className="text-center mt-10">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e4a9a] transition-colors"
            >
              Voir tous les articles
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-[#285BBB] font-semibold uppercase tracking-wider">Des questions particulières ?</span>
              <h2 className="text-4xl font-bold text-[#2C3C65] mt-3 mb-6">
                {contactSection.title || "Nous contacter"}
              </h2>
              <p className="text-lg text-[#6777A1] mb-8">
                N'hésitez pas à nous contacter pour toute question sur nos formations ou pour prendre rendez-vous.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#F5F7FF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#285BBB]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C3C65]">Adresse</h4>
                    <p className="text-[#6777A1]">{settings.address || '9 Allée Jean Jaurès, 13800 Istres'}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#ff3a2d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#ff3a2d]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C3C65]">Téléphone</h4>
                    <a href="tel:+33626716022" className="text-[#285BBB] hover:underline font-medium">
                      06 26 71 60 22
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#F5F7FF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#285BBB]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C3C65]">Email</h4>
                    <a 
                      href="mailto:contact@theenglishspeakingcenter.fr?cc=theenglishspeakingcenter@gmail.com"
                      className="inline-flex items-center gap-2 mt-1 px-4 py-2 bg-[#285BBB] text-white text-sm font-medium rounded-lg hover:bg-[#1e4a9a] transition-colors"
                    >
                      Envoyer un email
                    </a>
                  </div>
                </div>
              </div>
              {/* Google Maps */}
              <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.8944044847246!2d4.984486715532647!3d43.51510697912636!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b5f9c1f5b7c7d7%3A0x1234567890abcdef!2s9%20All%C3%A9e%20Jean%20Jaur%C3%A8s%2C%2013800%20Istres!5e0!3m2!1sfr!2sfr!4v1234567890123"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation"
                />
              </div>
            </div>
            <div>
              <div className="bg-[#F5F7FF] p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-[#2C3C65] mb-6">Envoyez-nous un message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nom</label>
                      <Input
                        value={contactForm.nom}
                        onChange={(e) => setContactForm({ ...contactForm, nom: e.target.value })}
                        required
                        className="w-full"
                        data-testid="contact-nom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Prénom</label>
                      <Input
                        value={contactForm.prenom}
                        onChange={(e) => setContactForm({ ...contactForm, prenom: e.target.value })}
                        required
                        className="w-full"
                        data-testid="contact-prenom"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">Email</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">Téléphone</label>
                    <Input
                      value={contactForm.telephone}
                      onChange={(e) => setContactForm({ ...contactForm, telephone: e.target.value })}
                      className="w-full"
                      data-testid="contact-telephone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">Votre message</label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={4}
                      className="w-full"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#285BBB] hover:bg-[#2C3C65] text-white py-3"
                    data-testid="contact-submit"
                  >
                    {loading ? 'Envoi...' : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#ff3a2d]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à démarrer votre apprentissage ?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {settings.inscription_open 
              ? settings.inscription_message || "Les inscriptions sont ouvertes pour l'année 2025-2026"
              : "Les inscriptions sont actuellement fermées"}
          </p>
          {settings.inscription_open && (
            <Link
              to="/inscription"
              className="inline-flex items-center px-8 py-4 bg-white text-[#ff3a2d] font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="cta-inscription"
            >
              Inscrivez-vous maintenant
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

// Composant principal avec Layout (Header + Footer)
const HomePage = () => {
  return (
    <Layout>
      <HomePageContent />
    </Layout>
  );
};

export default HomePage;
